import {useEffect, useMemo, useState} from 'react'
import {PartialEntry} from '@github-ui/react-core/partial-entry'
import {CreateIssueDialogEntry} from './CreateIssueDialogEntry'
import {VALUES} from '../constants/values'
import {noop} from '@github-ui/noop'
import {RelayEnvironmentProvider} from 'react-relay'
import {relayEnvironmentWithMissingFieldHandlerForNode} from '@github-ui/relay-environment'
import {type Root, createRoot} from 'react-dom/client'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {getSafeConfig} from '../utils/option-config'
import {AnalyticsProvider} from '@github-ui/analytics-provider'

export class IssueCreateDialogElement extends HTMLElement {
  root: Root | undefined
  connectedCallback() {
    const container = document.createElement('div')
    this.root = createRoot(container)
    this.append(container)
    this.render()
  }

  static get observedAttributes() {
    return ['isenabled', 'key', 'owner', 'repository', 'analytics-app-name', 'analytics-namespace']
  }

  attributeChangedCallback() {
    if (!this.isConnected) return
    this.render()
  }

  render() {
    const isEnabled = this.getAttribute('isenabled') === 'true'
    const key = this.getAttribute('key')
    const owner = this.getAttribute('owner')
    const repository = this.getAttribute('repository')
    const analyticsAppName = this.getAttribute('analytics-app-name')
    const analyticsNamespace = this.getAttribute('analytics-namespace')
    if (!this.root || !isEnabled || !key || key.length < 1) return

    const el = (
      <PartialEntry
        partialName="issue-create"
        embeddedData={{
          props: {isEnabled, key, owner, repository, analyticsAppName, analyticsNamespace} as CreateIssueModalProps,
        }}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Component={WrapperComponent as any}
        wasServerRendered={false}
        ssrError={Boolean(false)}
      />
    )

    this.root.render(el)
  }
}

const environment = relayEnvironmentWithMissingFieldHandlerForNode()

function WrapperComponent(props: CreateIssueModalProps) {
  return (
    <RelayEnvironmentProvider environment={environment}>
      <AnalyticsProvider
        appName={props.analyticsAppName ?? 'UNKNOWN'}
        category={props.analyticsNamespace ?? 'issue-create-web-component'}
        metadata={{}}
      >
        <CreateIssueModal {...props} />
      </AnalyticsProvider>
    </RelayEnvironmentProvider>
  )
}

type CreateIssueModalProps = {
  key: string
  owner?: string
  repository?: string
  analyticsAppName?: string
  analyticsNamespace?: string
}

function CreateIssueModal({key, owner, repository}: CreateIssueModalProps) {
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    setIsVisible(true)
  }, [key])

  const repo = useMemo(
    () =>
      owner && repository
        ? {
            repository: {
              owner,
              name: repository,
            },
          }
        : undefined,
    [owner, repository],
  )

  if (!isVisible) return null

  // TODO: setup real value for the config below
  return (
    <CreateIssueDialogEntry
      navigate={noop}
      isCreateDialogOpen={isVisible}
      setIsCreateDialogOpen={setIsVisible}
      optionConfig={getSafeConfig({
        storageKeyPrefix: VALUES.storageKeyPrefixes.globalAdd,
        pasteUrlsAsPlainText: false,
        singleKeyShortcutsEnabled: true,
        useMonospaceFont: false,
        issueCreateArguments: repo,
        showFullScreenButton: false,
      })}
    />
  )
}

if (ssrSafeWindow && !ssrSafeWindow.customElements.get('issue-create-dialog')) {
  ssrSafeWindow.customElements.define('issue-create-dialog', IssueCreateDialogElement)
}

try{ WrapperComponent.displayName ||= 'WrapperComponent' } catch {}
try{ CreateIssueModal.displayName ||= 'CreateIssueModal' } catch {}